import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  Wand2,
  Loader2,
  Copy,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API } from "./utils";

const KIND_LABELS = {
  weekly_pack: {
    label: "Wochen-Content (7 Posts)",
    icon: Sparkles,
    hint: "Insta + TikTok Posts für eine ganze Woche",
  },
  reel_script: {
    label: "3 Reel-Skripte",
    icon: Wand2,
    hint: "POV / Stress-Test / Education",
  },
  story_ideas: {
    label: "10 Story-Ideen",
    icon: Sparkles,
    hint: "Polls, BTS, Engagement",
  },
};

const ContentStudio = () => {
  const { token } = useAuth();
  const [generating, setGenerating] = useState(null);
  const [extraContext, setExtraContext] = useState("");
  const [history, setHistory] = useState([]);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/content/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(res.data);
      if (res.data.length && !selectedDraft) setSelectedDraft(res.data[0]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async (kind) => {
    setGenerating(kind);
    try {
      const res = await axios.post(
        `${API}/admin/content/generate`,
        { kind, extra_context: extraContext || null },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 90000,
        }
      );
      const draft = res.data;
      setSelectedDraft(draft);
      setHistory((prev) => [draft, ...prev]);
      setExtraContext("");
      toast.success(`${KIND_LABELS[kind].label} generiert!`);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Fehler: ${detail}`);
    } finally {
      setGenerating(null);
    }
  };

  const copyContent = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("In Zwischenablage kopiert");
  };

  const deleteDraft = async (id) => {
    try {
      await axios.delete(`${API}/admin/content/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory((prev) => prev.filter((d) => d.id !== id));
      if (selectedDraft?.id === id) setSelectedDraft(null);
      toast.success("Gelöscht");
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  return (
    <section data-testid="content-studio-section" className="space-y-6">
      <div className="border border-white/10 bg-[#0A0A0A] p-6">
        <div className="flex items-center gap-3 mb-3">
          <Sparkles className="w-5 h-5 text-[#00A651]" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
            Content Studio
          </h2>
          <span className="text-[10px] font-mono text-white/40 ml-auto">claude-sonnet-4.5</span>
        </div>
        <p className="text-white/60 text-sm leading-relaxed mb-5">
          Generiere Insta-/TikTok-Content auf Knopfdruck. Powered by Claude Sonnet — auf 4keeperz
          Brand-Tonality trainiert.
        </p>

        <label className="block font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-2">
          Optionaler Kontext für diesen Batch (z.B. "Fokus auf Mamba", "neue Lieferung", "FCW
          Spieltag")
        </label>
        <input
          type="text"
          value={extraContext}
          onChange={(e) => setExtraContext(e.target.value)}
          placeholder="leer lassen für Standard-Mix"
          maxLength={500}
          data-testid="content-extra-context"
          className="w-full bg-black border border-white/15 focus:border-[#00A651] outline-none px-4 py-3 text-white text-sm transition-colors mb-5"
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(KIND_LABELS).map(([kind, meta]) => {
            const Icon = meta.icon;
            const isGen = generating === kind;
            return (
              <button
                key={kind}
                onClick={() => generate(kind)}
                disabled={!!generating}
                data-testid={`content-generate-${kind}`}
                className="text-left border border-white/15 bg-black hover:border-[#00A651] hover:bg-[#00A651]/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors p-5 group"
              >
                <div className="flex items-center gap-2 mb-2">
                  {isGen ? (
                    <Loader2 className="w-4 h-4 text-[#00A651] animate-spin" />
                  ) : (
                    <Icon className="w-4 h-4 text-[#00A651]" />
                  )}
                  <span className="font-condensed uppercase tracking-widest text-xs text-white">
                    {meta.label}
                  </span>
                </div>
                <p className="text-white/50 text-xs leading-relaxed">
                  {isGen ? "Wird generiert (10-30 Sek)..." : meta.hint}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div
          className="lg:col-span-4 border border-white/10 bg-[#0A0A0A] flex flex-col"
          style={{ minHeight: 400 }}
        >
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <span className="font-condensed uppercase tracking-widest text-[10px] text-white/50">
              Verlauf ({history.length})
            </span>
            <button
              onClick={fetchHistory}
              className="text-white/40 hover:text-[#00A651] transition-colors"
              data-testid="content-refresh-history"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
          {history.length === 0 ? (
            <div className="flex-1 flex items-center justify-center text-white/40 text-xs p-6 text-center">
              Noch keine generierten Inhalte. Klick oben auf einen Button.
            </div>
          ) : (
            <ul className="overflow-y-auto flex-1" data-testid="content-history-list">
              {history.map((d) => {
                const meta = KIND_LABELS[d.kind] || {};
                const active = selectedDraft?.id === d.id;
                return (
                  <li
                    key={d.id}
                    onClick={() => setSelectedDraft(d)}
                    className={`px-4 py-3 border-b border-white/5 cursor-pointer transition-colors ${
                      active
                        ? "bg-[#00A651]/10 border-l-2 border-l-[#00A651]"
                        : "hover:bg-black/40"
                    }`}
                    data-testid={`content-history-item-${d.id}`}
                  >
                    <div className="font-condensed uppercase tracking-wider text-xs text-white">
                      {meta.label || d.kind}
                    </div>
                    <div className="text-white/40 text-[10px] mt-1">
                      {new Date(d.created_at).toLocaleString("de-CH")}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="lg:col-span-8 border border-white/10 bg-[#0A0A0A]" data-testid="content-viewer">
          {!selectedDraft ? (
            <div className="p-16 text-center text-white/40 text-sm">
              Wähle einen Eintrag aus dem Verlauf oder generiere etwas Neues.
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between gap-3">
                <div>
                  <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50">
                    {(KIND_LABELS[selectedDraft.kind] || {}).label}
                  </div>
                  <div className="text-white/40 text-[10px] mt-0.5">
                    {new Date(selectedDraft.created_at).toLocaleString("de-CH")}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => copyContent(selectedDraft.content)}
                    className="inline-flex items-center gap-2 border border-white/15 hover:border-[#00A651] hover:text-[#00A651] text-white/70 font-condensed uppercase tracking-widest text-[10px] px-3 py-2 transition-colors"
                    data-testid="content-copy-all"
                  >
                    <Copy className="w-3 h-3" /> Alles kopieren
                  </button>
                  <button
                    onClick={() => deleteDraft(selectedDraft.id)}
                    className="inline-flex items-center gap-2 border border-white/15 hover:border-red-400 hover:text-red-400 text-white/70 font-condensed uppercase tracking-widest text-[10px] px-3 py-2 transition-colors"
                    data-testid="content-delete"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <pre
                className="p-5 text-white/85 text-sm leading-relaxed whitespace-pre-wrap font-sans overflow-x-auto"
                style={{ maxHeight: "70vh" }}
                data-testid="content-body"
              >
                {selectedDraft.content}
              </pre>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContentStudio;
